import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f9fafb',
  red:    '#dc2626',
  green:  '#16a34a',
};

const fmtEur = v =>
  (!v && v !== 0) ? '–'
  : Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const fmtDate = d =>
  d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–';

const todayISO = () => new Date().toISOString().split('T')[0];

function sixMonthsAgo() {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d.toISOString().split('T')[0];
}

const KATEGORIEN_EINNAHME = ['Miete', 'Kaution', 'Betriebskostenvorauszahlung', 'Nachzahlung', 'Sonstige Einnahme'];
const KATEGORIEN_AUSGABE  = ['Reparatur & Instandhaltung', 'Verwaltungskosten', 'Versicherung', 'Steuer & Abgaben', 'Betriebskosten', 'Zinsen & Tilgung', 'Sonstige Ausgabe'];
const MWST_OPTIONS = ['0', '7', '19'];

const STATUS_CFG = {
  offen:        { label: 'Offen',      color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  bezahlt:      { label: 'Bezahlt',    color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  ueberfaellig: { label: 'Überfällig', color: '#dc2626', bg: '#fff1f2', border: '#fecaca' },
};

function Badge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.offen;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99,
      background: cfg.bg, border: `1.5px solid ${cfg.border}`, color: cfg.color,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
}

function Toggle({ value, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
      <div
        onClick={() => onChange(!value)}
        style={{
          width: 40, height: 22, borderRadius: 11, position: 'relative', cursor: 'pointer',
          background: value ? T.ink : '#d1d5db', transition: 'background 0.2s',
        }}
      >
        <div style={{
          width: 16, height: 16, borderRadius: '50%', background: 'white',
          position: 'absolute', top: 3, left: value ? 21 : 3,
          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
      <span style={{ fontSize: 14, color: T.ink }}>{label}</span>
    </label>
  );
}

/* ── Modal overlay ── */
function Modal({ onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 24,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'white', borderRadius: 14, width: '100%', maxWidth: 900,
        maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        {children}
      </div>
    </div>
  );
}

/* ── Neuen Umsatz Modal ── */
function UmsatzModal({ onClose, onSave, properties, kontakte, isMobile }) {
  const [typ,           setTyp]           = useState('ausgabe');
  const [lieferantQ,    setLieferantQ]    = useState('');
  const [lieferantId,   setLieferantId]   = useState('');
  const [beschreibung,  setBeschreibung]  = useState('');
  const [belegnummer,   setBelegnummer]   = useState('');
  const [belegdatum,    setBelegdatum]    = useState(todayISO());
  const [faelligkeit,   setFaelligkeit]   = useState('');
  const [alsBezahlt,    setAlsBezahlt]    = useState(false);
  const [cashflow,      setCashflow]      = useState(true);
  const [propertyId,    setPropertyId]    = useState('');
  const [kategorie,     setKategorie]     = useState('');
  const [mwst,          setMwst]          = useState('19');
  const [betrag,        setBetrag]        = useState('');
  const [showKontakte,  setShowKontakte]  = useState(false);
  const [saving,        setSaving]        = useState(false);
  const { user } = useAuth();
  const { addToast } = useToast();

  const kategorien = typ === 'einnahme' ? KATEGORIEN_EINNAHME : KATEGORIEN_AUSGABE;
  const filteredKontakte = kontakte.filter(k => {
    const name = k.typ === 'unternehmen' ? (k.firmenname || '') : `${k.vorname || ''} ${k.nachname || ''}`.trim();
    return name.toLowerCase().includes(lieferantQ.toLowerCase());
  });

  async function handleSave() {
    if (!betrag || isNaN(parseFloat(betrag))) { addToast('Bitte Betrag eingeben', 'error'); return; }
    setSaving(true);
    const { error } = await supabase.from('buchungen').insert({
      owner_id: user.id,
      typ,
      lieferant_id: lieferantId || null,
      beschreibung,
      belegnummer,
      belegdatum: belegdatum || null,
      faelligkeit: faelligkeit || null,
      als_bezahlt: alsBezahlt,
      cashflowrelevant: cashflow,
      property_id: propertyId || null,
      kategorie,
      mwst: parseFloat(mwst),
      betrag: parseFloat(betrag.replace(',','.')),
      status: alsBezahlt ? 'bezahlt' : 'offen',
    });
    setSaving(false);
    if (error) { addToast('Fehler beim Speichern', 'error'); return; }
    addToast('Umsatz angelegt', 'success');
    onSave();
  }

  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 7, border: `1px solid ${T.border}`,
    fontSize: 14, color: T.ink, outline: 'none', background: 'white', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: T.muted, marginBottom: 5, display: 'block' };

  return (
    <Modal onClose={onClose}>
      {/* Header */}
      <div style={{ padding: '20px 28px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.ink }}>Neuen Umsatz anlegen</h2>
      </div>

      {/* Body */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', flexDirection: isMobile ? 'column' : 'row' }}>
        {/* Left: file upload */}
        {!isMobile && <div style={{
          width: 280, flexShrink: 0, padding: 28, borderRight: `1px solid ${T.border}`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 16, background: T.bg,
        }}>
          <div style={{
            width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="16 16 12 12 8 16"/>
              <line x1="12" y1="12" x2="12" y2="21"/>
              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
            </svg>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.ink, marginBottom: 6 }}>Belege hochladen</div>
            <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>Datei hierhin ziehen oder auswählen<br/>(max. 25 MB)</div>
          </div>
          <button style={{
            width: '100%', padding: '9px 16px', borderRadius: 8,
            border: `1.5px solid ${T.border}`, background: 'white',
            color: T.ink, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>
            Aus ImmoHQ auswählen
          </button>
          <label style={{
            width: '100%', padding: '9px 16px', borderRadius: 8,
            border: `1.5px solid ${T.border}`, background: 'white',
            color: T.ink, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            textAlign: 'center', display: 'block',
          }}>
            Vom Gerät hochladen
            <input type="file" style={{ display: 'none' }} accept="image/*,.pdf" />
          </label>
        </div>}

        {/* Right: form */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Typ tabs */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, borderRadius: 8, overflow: 'hidden', border: `1px solid ${T.border}` }}>
            {['einnahme','ausgabe'].map(t => (
              <button
                key={t}
                onClick={() => { setTyp(t); setKategorie(''); }}
                style={{
                  padding: '10px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
                  background: typ === t ? T.ink : '#f3f4f6',
                  color: typ === t ? 'white' : T.muted,
                  transition: 'background 0.15s',
                }}
              >
                {t === 'einnahme' ? 'Einnahme' : 'Ausgabe'}
              </button>
            ))}
          </div>

          {/* Lieferant / Kontakt */}
          <div style={{ position: 'relative' }}>
            <label style={labelStyle}>{typ === 'einnahme' ? 'Mieter / Absender' : 'Lieferanten auswählen'}</label>
            <input
              value={lieferantId
                ? (kontakte.find(k => k.id === lieferantId)
                    ? (kontakte.find(k => k.id === lieferantId).typ === 'unternehmen'
                        ? kontakte.find(k => k.id === lieferantId).firmenname
                        : `${kontakte.find(k => k.id === lieferantId).vorname || ''} ${kontakte.find(k => k.id === lieferantId).nachname || ''}`.trim())
                    : '')
                : lieferantQ}
              onChange={e => { setLieferantQ(e.target.value); setLieferantId(''); setShowKontakte(true); }}
              onFocus={() => setShowKontakte(true)}
              placeholder="Kontakte durchsuchen..."
              style={inputStyle}
            />
            {showKontakte && lieferantQ && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                background: 'white', border: `1px solid ${T.border}`, borderRadius: 8,
                boxShadow: '0 4px 16px rgba(0,0,0,0.10)', zIndex: 20, maxHeight: 200, overflowY: 'auto',
              }}>
                {filteredKontakte.length === 0
                  ? <div style={{ padding: '12px 16px', fontSize: 13, color: T.muted }}>Keine Kontakte gefunden</div>
                  : filteredKontakte.map(k => {
                      const name = k.typ === 'unternehmen' ? (k.firmenname || '–') : `${k.vorname || ''} ${k.nachname || ''}`.trim() || '–';
                      return (
                        <div key={k.id}
                          onMouseDown={() => { setLieferantId(k.id); setLieferantQ(''); setShowKontakte(false); }}
                          style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 14, color: T.ink }}
                          onMouseEnter={e => e.currentTarget.style.background = T.bg}
                          onMouseLeave={e => e.currentTarget.style.background = 'white'}
                        >
                          {name}
                        </div>
                      );
                    })
                }
              </div>
            )}
          </div>

          {/* Beschreibung */}
          <div>
            <label style={labelStyle}>Beschreibung</label>
            <input value={beschreibung} onChange={e => setBeschreibung(e.target.value)} style={inputStyle} placeholder="Beschreibung eingeben" />
          </div>

          {/* Belegnummer + Belegdatum */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Belegnummer</label>
              <input value={belegnummer} onChange={e => setBelegnummer(e.target.value)} style={inputStyle} placeholder="z.B. RE-2026-001" />
            </div>
            <div>
              <label style={labelStyle}>Belegdatum</label>
              <input type="date" value={belegdatum} onChange={e => setBelegdatum(e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* Fälligkeit */}
          <div>
            <label style={labelStyle}>Fälligkeit</label>
            <input type="date" value={faelligkeit} onChange={e => setFaelligkeit(e.target.value)} style={{ ...inputStyle, color: faelligkeit ? T.ink : T.muted }} placeholder="DD.MM.YYYY" />
          </div>

          {/* Toggles */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Toggle value={alsBezahlt} onChange={setAlsBezahlt} label="Als bezahlt markieren" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Toggle value={cashflow} onChange={setCashflow} label="Cashflowrelevant" />
              <button style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }} title="Relevant für die Cashflow-Auswertung">?</button>
            </div>
          </div>

          {/* Objekt, Kategorie, MwSt, Betrag */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1.2fr 0.8fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Objekt-Einheit</label>
              <select value={propertyId} onChange={e => setPropertyId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">Einheit auswählen</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Kategorie</label>
              <select value={kategorie} onChange={e => setKategorie(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">Kategorie wählen</option>
                {kategorien.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>MwSt.</label>
              <select value={mwst} onChange={e => setMwst(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                {MWST_OPTIONS.map(m => <option key={m} value={m}>{m} %</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Betrag</label>
              <input
                value={betrag}
                onChange={e => setBetrag(e.target.value)}
                placeholder="0,00 €"
                style={{ ...inputStyle, textAlign: 'right' }}
              />
            </div>
          </div>

          {/* Betrag aufteilen */}
          <div style={{ textAlign: 'right' }}>
            <button style={{ background: 'none', border: 'none', color: T.ink, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
              + Betrag aufteilen
            </button>
          </div>

          {/* OCR scan */}
          <button style={{
            alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '9px 18px', borderRadius: 8, border: `1px solid ${T.border}`,
            background: T.bg, color: T.muted, fontSize: 13, fontWeight: 500, cursor: 'pointer',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            Beleg mit OCR scannen (BETA)
          </button>

          {/* Gesamtbetrag */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
            <span style={{ fontSize: 14, color: T.muted, fontWeight: 600 }}>Gesamtbetrag :</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>
              {betrag ? fmtEur(parseFloat(betrag.replace(',','.'))) : '0,00 €'}
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: '16px 28px', borderTop: `1px solid ${T.border}`, flexShrink: 0,
        display: 'flex', justifyContent: 'flex-end', gap: 12,
      }}>
        <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'none', color: T.ink, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          Abbrechen
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{ padding: '9px 24px', borderRadius: 8, border: 'none', background: T.ink, color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}
        >
          {saving ? 'Speichert…' : 'Umsatz anlegen'}
        </button>
      </div>
    </Modal>
  );
}

/* ── Empty state ── */
function EmptyState({ text, sub, onAdd, isMobile }) {
  return (
    <div style={{ padding:'48px 40px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', gap:32, marginBottom:40 }}>
        <div style={{ flexShrink:0, width:64, height:64, background:'#f7f7f7', borderRadius:16, display:'flex', alignItems:'center', justifyContent:'center', border:'1.5px solid e5e5e5' }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
          </svg>
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:20, fontWeight:700, color:T.ink, marginBottom:8, letterSpacing:'-0.3px' }}>{text}</div>
          <div style={{ fontSize:14, color:T.muted, lineHeight:1.8, maxWidth:520 }}>{sub}</div>
          {onAdd && <button onClick={onAdd} className="btn-primary" style={{ marginTop:20 }}>+ Erste Buchung anlegen</button>}
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap:16 }}>
        {[
          { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>, title:'Einnahmen & Ausgaben', text:'Erfasse alle Mieteinnahmen und Betriebskosten übersichtlich.' },
          { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>, title:'Jahresübersicht', text:'Filtere nach Zeiträumen und erhalte klare Jahresauswertungen.' },
          { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>, title:'Export', text:'Exportiere Buchungen als CSV für Steuerberater oder Excel.' },
        ].map((f,i)=>(
          <div key={i} style={{ background:'#f7f7f7', borderRadius:12, padding:'18px 20px', border:`1px solid ${T.border}` }}>
            <div style={{ width:36, height:36, background:'white', borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:12, border:`1px solid ${T.border}`, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>{f.icon}</div>
            <div style={{ fontSize:13, fontWeight:700, color:T.ink, marginBottom:4 }}>{f.title}</div>
            <div style={{ fontSize:12, color:T.muted, lineHeight:1.6 }}>{f.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── KPI Card ── */
function KpiCard({ label, value, color }) {
  return (
    <div style={{
      flex: 1, minWidth: 180, padding: '20px 24px', background: 'white',
      borderRadius: 10, border: `1px solid ${T.border}`,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 14, color: T.muted, fontWeight: 500 }}>{label}</span>
        <button style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}>?</button>
      </div>
      <div style={{ fontSize: 32, fontWeight: 800, color: color || T.ink }}>{value}</div>
    </div>
  );
}

/* ══════════════════════════════════════
   Main Finanzen page
══════════════════════════════════════ */
export default function Finanzen() {
  const { user } = useAuth();
  const { isMobile } = useBreakpoint();

  const [activeTab,   setActiveTab]   = useState('einnahmen'); // 'einnahmen' | 'mietuebersicht'
  const [showModal,   setShowModal]   = useState(false);

  const [buchungen,   setBuchungen]   = useState([]);
  const [properties,  setProperties]  = useState([]);
  const [kontakte,    setKontakte]    = useState([]);
  const [loading,     setLoading]     = useState(true);

  /* filters */
  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterProp,   setFilterProp]   = useState('');
  const [filterKat,    setFilterKat]    = useState('');
  const [dateFrom,     setDateFrom]     = useState(sixMonthsAgo());
  const [dateTo,       setDateTo]       = useState(todayISO());

  const [openStatus,  setOpenStatus]  = useState(false);
  const [openProp,    setOpenProp]    = useState(false);
  const [openKat,     setOpenKat]     = useState(false);

  /* close dropdowns on outside click */
  useEffect(() => {
    const close = () => { setOpenStatus(false); setOpenProp(false); setOpenKat(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [bRes, pRes, kRes] = await Promise.all([
      supabase.from('buchungen').select('*').eq('owner_id', user.id).order('belegdatum', { ascending: false }),
      supabase.from('properties').select('id,name,city').eq('owner_id', user.id).order('name'),
      supabase.from('kontakte').select('id,typ,vorname,nachname,firmenname').eq('owner_id', user.id).order('nachname'),
    ]);
    setBuchungen(bRes.data || []);
    setProperties(pRes.data || []);
    setKontakte(kRes.data || []);
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  /* ── filtered Einnahmen/Ausgaben ── */
  const filteredBuchungen = buchungen.filter(b => {
    if (search && !`${b.beschreibung||''} ${b.belegnummer||''} ${b.kategorie||''}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStatus && b.status !== filterStatus) return false;
    if (filterProp && b.property_id !== filterProp) return false;
    if (filterKat && b.kategorie !== filterKat) return false;
    if (dateFrom && b.belegdatum && b.belegdatum < dateFrom) return false;
    if (dateTo   && b.belegdatum && b.belegdatum > dateTo)   return false;
    return true;
  });

  function exportCsv() {
    const rows = [['Typ','Datum','Beschreibung','Belegnummer','Kategorie','Betrag','Status']];
    filteredBuchungen.forEach(b => {
      rows.push([b.typ, fmtDate(b.belegdatum), b.beschreibung||'', b.belegnummer||'', b.kategorie||'', fmtEur(b.betrag), STATUS_CFG[b.status]?.label||'']);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['\uFEFF'+csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `Finanzen_${todayISO()}.csv`;
    a.click();
  }

  const inputStyle = {
    padding: '7px 12px', borderRadius: 7, border: `1px solid ${T.border}`,
    fontSize: 13, color: T.ink, outline: 'none', background: 'white',
  };

  /* dropdown */
  function Dd({ label, open, onToggle, children, width = 180 }) {
    return (
      <div style={{ position: 'relative' }} onMouseDown={e => e.stopPropagation()}>
        <button onClick={onToggle} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          ...inputStyle, cursor: 'pointer', fontWeight: 500,
        }}>
          {label}
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        {open && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 4,
            background: 'white', border: `1px solid ${T.border}`, borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.10)', zIndex: 50, minWidth: width, padding: 6,
          }}>
            {children}
          </div>
        )}
      </div>
    );
  }

  function DdItem({ label, selected, onClick }) {
    return (
      <div onClick={onClick} style={{
        padding: '8px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
        background: selected ? '#f5f5f5' : 'transparent', color: selected ? T.ink : T.ink, fontWeight: selected ? 600 : 400,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = T.bg; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
      >
        {label}
        {selected && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
      </div>
    );
  }

  const allKategorien = [...KATEGORIEN_EINNAHME, ...KATEGORIEN_AUSGABE];

  const fmtDateShort = d => {
    if (!d) return '';
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", overflowX: 'hidden', maxWidth: '100%' }}>
      {/* Left sub-nav panel */}
      {!isMobile && (
        <div style={{
          width: 220, flexShrink: 0, borderRight: `1px solid ${T.border}`,
          background: 'white', padding: '28px 0',
        }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: T.ink, padding: '0 20px', marginBottom: 20 }}>Finanzen</div>

          {/* Einnahmen / Ausgaben */}
          <div
            onClick={() => setActiveTab('einnahmen')}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '9px 20px', cursor: 'pointer',
              background: activeTab === 'einnahmen' ? '#f5f5f5' : 'transparent',
              borderRight: activeTab === 'einnahmen' ? `3px solid ${T.ink}` : '3px solid transparent',
            }}
            onMouseEnter={e => { if (activeTab !== 'einnahmen') e.currentTarget.style.background = T.bg; }}
            onMouseLeave={e => { if (activeTab !== 'einnahmen') e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ fontSize: 14, fontWeight: activeTab === 'einnahmen' ? 700 : 500, color: activeTab === 'einnahmen' ? T.ink : T.ink }}>
              Einnahmen / Ausgaben
            </span>
            <button
              onClick={e => { e.stopPropagation(); setActiveTab('einnahmen'); setShowModal(true); }}
              style={{
                width: 22, height: 22, borderRadius: 5, border: `1px solid ${T.border}`,
                background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: T.muted, fontSize: 16, lineHeight: 1, padding: 0, flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = T.bg; e.currentTarget.style.color = T.ink; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = T.muted; }}
            >+</button>
          </div>

        </div>
      )}

      {/* Main content */}
      <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? '16px' : '24px 28px' }}>

        {/* Mobile header (sidebar hidden) */}
        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.ink }}>Finanzen</div>
            <button
              onClick={() => setShowModal(true)}
              style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: T.ink, color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
            >+ Neu</button>
          </div>
        )}

        {/* ══ EINNAHMEN / AUSGABEN ══ */}
        {activeTab === 'einnahmen' && (
          <>
            {/* Filter bar */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, alignItems: 'center' }}
              onMouseDown={e => e.stopPropagation()}>
              {/* Search */}
              <div style={{ position: 'relative' }}>
                <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                  width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Suchen"
                  style={{ ...inputStyle, paddingLeft: 30, width: isMobile ? '100%' : 200, minWidth: 0 }} />
              </div>

              {/* Date range (desktop only) */}
              {!isMobile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, ...inputStyle, padding: '6px 10px' }}>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  style={{ border: 'none', outline: 'none', fontSize: 13, color: T.ink, background: 'transparent', width: 110 }} />
                <span style={{ color: T.muted, fontSize: 12 }}>~</span>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  style={{ border: 'none', outline: 'none', fontSize: 13, color: T.ink, background: 'transparent', width: 110 }} />
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
              )}

              {/* Status */}
              <Dd label={filterStatus ? STATUS_CFG[filterStatus]?.label : 'Status'} open={openStatus}
                onToggle={() => { setOpenStatus(o => !o); setOpenProp(false); setOpenKat(false); }}>
                <DdItem label="Alle" selected={!filterStatus} onClick={() => { setFilterStatus(''); setOpenStatus(false); }} />
                {Object.entries(STATUS_CFG).map(([k,v]) => (
                  <DdItem key={k} label={v.label} selected={filterStatus === k} onClick={() => { setFilterStatus(k); setOpenStatus(false); }} />
                ))}
              </Dd>

              {/* Objekt */}
              <Dd label={filterProp ? (properties.find(p => p.id === filterProp)?.name || 'Objekt') : 'Alle Objekte/Einheiten'}
                open={openProp} width={220}
                onToggle={() => { setOpenProp(o => !o); setOpenStatus(false); setOpenKat(false); }}>
                <DdItem label="Alle Objekte" selected={!filterProp} onClick={() => { setFilterProp(''); setOpenProp(false); }} />
                {properties.map(p => <DdItem key={p.id} label={p.name} selected={filterProp === p.id} onClick={() => { setFilterProp(p.id); setOpenProp(false); }} />)}
              </Dd>

              {/* Kategorie */}
              <Dd label={filterKat || 'Alle Kategorien'} open={openKat} width={220}
                onToggle={() => { setOpenKat(o => !o); setOpenStatus(false); setOpenProp(false); }}>
                <DdItem label="Alle Kategorien" selected={!filterKat} onClick={() => { setFilterKat(''); setOpenKat(false); }} />
                {allKategorien.map(k => <DdItem key={k} label={k} selected={filterKat === k} onClick={() => { setFilterKat(k); setOpenKat(false); }} />)}
              </Dd>
            </div>

            {/* Second filter row: funnel + export */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              <button style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 36, height: 36, borderRadius: 7, border: `1px solid ${T.border}`,
                background: 'white', cursor: 'pointer',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
              </button>
              <button onClick={exportCsv} style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 36, height: 36, borderRadius: 7, border: `1px solid ${T.border}`,
                background: 'white', cursor: 'pointer',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </button>
            </div>

            {/* Table */}
            {loading ? (
              isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[...Array(4)].map((_,i) => (
                    <div key={i} style={{ background: 'white', borderRadius: 10, border: `1px solid ${T.border}`, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ width: 50, height: 13, borderRadius: 6, background: '#f3f4f6' }} />
                        <div style={{ width: 70, height: 13, borderRadius: 6, background: '#f3f4f6' }} />
                      </div>
                      <div style={{ width: '60%', height: 13, borderRadius: 6, background: '#f3f4f6' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ width: 80, height: 13, borderRadius: 6, background: '#f3f4f6' }} />
                        <div style={{ width: 60, height: 13, borderRadius: 6, background: '#f3f4f6' }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ background: 'white', borderRadius: 10, border: `1px solid ${T.border}` }}>
                  {[...Array(4)].map((_,i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr 100px 110px', gap: 12, padding: '14px 20px', borderBottom: i < 3 ? `1px solid ${T.border}` : 'none' }}>
                      {[...Array(6)].map((_,j) => <div key={j} style={{ height: 13, borderRadius: 6, background: '#f3f4f6' }} />)}
                    </div>
                  ))}
                </div>
              )
            ) : filteredBuchungen.length === 0 ? (
              <EmptyState
                text="Keine Buchungen verfügbar"
                sub="Keine Einnahmen oder Ausgaben verfügbar. Lege jetzt Deine erste Einnahme oder Ausgabe manuell an."
                isMobile={isMobile}
              />
            ) : (
              <>
                {/* Desktop table */}
                {!isMobile && (
                  <div style={{ background: 'white', borderRadius: 10, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
                    {/* Header */}
                    <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr 110px 120px', padding: '10px 20px', background: T.bg, borderBottom: `1px solid ${T.border}` }}>
                      {['Typ','Datum','Beschreibung','Kategorie','Betrag','Status'].map((h,i) => (
                        <div key={h} style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: i === 4 ? 'right' : 'left', paddingRight: i === 4 ? 8 : 0 }}>{h}</div>
                      ))}
                    </div>
                    {filteredBuchungen.map((b, i) => (
                      <div key={b.id} style={{
                        display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr 110px 120px',
                        alignItems: 'center', padding: '13px 20px',
                        borderBottom: i < filteredBuchungen.length - 1 ? `1px solid ${T.border}` : 'none',
                        background: 'white', transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = T.bg}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}
                      >
                        <div>
                          <span style={{
                            fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 99,
                            background: b.typ === 'einnahme' ? '#f0fdf4' : '#fff1f2',
                            color: b.typ === 'einnahme' ? T.green : T.red,
                            border: `1.5px solid ${b.typ === 'einnahme' ? '#bbf7d0' : '#fecaca'}`,
                          }}>
                            {b.typ === 'einnahme' ? '+ EIN' : '– AUS'}
                          </span>
                        </div>
                        <div style={{ fontSize: 13, color: T.muted }}>{fmtDate(b.belegdatum)}</div>
                        <div style={{ fontSize: 14, color: T.ink }}>{b.beschreibung || '–'}</div>
                        <div style={{ fontSize: 13, color: T.muted }}>{b.kategorie || '–'}</div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: b.typ === 'einnahme' ? T.green : T.red, textAlign: 'right', paddingRight: 8 }}>
                          {b.typ === 'einnahme' ? '+' : '-'}{fmtEur(b.betrag)}
                        </div>
                        <div><Badge status={b.status} /></div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Mobile card list */}
                {isMobile && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {filteredBuchungen.map(b => (
                      <div key={b.id} style={{
                        background: 'white', borderRadius: 10, border: `1px solid ${T.border}`,
                        padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
                      }}>
                        {/* Top row: typ badge + betrag */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{
                            fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 99,
                            background: b.typ === 'einnahme' ? '#f0fdf4' : '#fff1f2',
                            color: b.typ === 'einnahme' ? T.green : T.red,
                            border: `1.5px solid ${b.typ === 'einnahme' ? '#bbf7d0' : '#fecaca'}`,
                          }}>
                            {b.typ === 'einnahme' ? '+ EIN' : '– AUS'}
                          </span>
                          <span style={{ fontSize: 15, fontWeight: 700, color: b.typ === 'einnahme' ? T.green : T.red }}>
                            {b.typ === 'einnahme' ? '+' : '-'}{fmtEur(b.betrag)}
                          </span>
                        </div>
                        {/* Beschreibung */}
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{b.beschreibung || '–'}</div>
                        {/* Bottom row: datum + status */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 12, color: T.muted }}>{fmtDate(b.belegdatum)}</span>
                          <Badge status={b.status} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

      </div>

      {/* Neuen Umsatz Modal */}
      {showModal && (
        <UmsatzModal
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); load(); }}
          properties={properties}
          kontakte={kontakte}
          isMobile={isMobile}
        />
      )}
    </div>
  );
}
